import React from 'react';
import { differenceInCalendarDays, format, isSameDay, startOfDay, type Locale } from 'date-fns';
import { Lead, Category } from '../../types';
import { useI18n } from '../../context/I18nContext';
import { useDateLocale } from '../../hooks/useDateLocale';
import ContextMenu, { ContextMenuTarget } from '../ui/ContextMenu';
import ContactIconWithCategory from '../ui/ContactIconWithCategory';
import { getLeadActionMenuItems, type LeadMenuActions } from '../../utils/leadActions';
import type { FollowUpTab } from './FollowUpSection';
import { FOLLOWUP_URGENCY_PIPELINE_STAGE, STAGE_ICON_VISUAL, followUpUrgencyBadgeVisual } from '../../styles/stageVisual';
import { Calendar, ChevronRight, Mail, MessageSquare } from 'lucide-react';
import ActionIconButton from '../ui/ActionIconButton';

export const FOLLOWUP_PREVIEW_LIMIT = 5;

/** Fixed row slot height — 5 slots = stable list body. */
const FOLLOWUP_ROW_SLOT_H = 'h-[2.75rem]';
/** Fixed footer slot for « Voir les X autres › ». */
const FOLLOWUP_LIST_FOOTER_H = 'h-[1.75rem]';
/**
 * Column grid: icon | name | date | flex | badge (fixed, left) | flex | actions
 * Mobile: only icon | name | actions (hidden cells leave the flow — keep 3 tracks).
 * Equal spacers center the badge column; badges left-align inside it.
 */
const FOLLOWUP_ROW_GRID =
  'grid w-full shrink-0 grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-x-2 overflow-hidden py-1.5 sm:grid-cols-[2rem_15rem_6.75rem_minmax(0,1fr)_7.5rem_minmax(0,1fr)_auto] sm:gap-x-3';

const FOLLOWUP_DATE_CELL =
  'hidden min-w-0 truncate text-left text-sm tabular-nums text-gray-500 sm:block';

interface FollowUpDateCellProps {
  date: Date;
  isToday: boolean;
  todayLabel: string;
  locale: Locale;
}

const FollowUpDateCell: React.FC<FollowUpDateCellProps> = ({ date, isToday, todayLabel, locale }) => {
  const label = isToday ? todayLabel : format(date, 'd MMM yyyy', { locale });

  return (
    <span className={FOLLOWUP_DATE_CELL} aria-label={isToday ? todayLabel : format(date, 'P', { locale })}>
      {label}
    </span>
  );
};

interface FollowUpListProps {
  leads: Lead[];
  tab: FollowUpTab;
  categories: Category[];
  leadMenuActions?: LeadMenuActions;
  projects?: any[];
  onEdit: (lead: Lead) => void;
  onDelete: (leadId: string) => void;
  onSeeOthers: () => void;
  onLeadClick?: (lead: Lead) => void;
}

const VERY_LATE_DAYS = 7;

function followUpStatus(
  tab: FollowUpTab,
  nextActionAt: Date,
  today: Date,
): { labelKey: string; variant: 'info' | 'orange-pastel' | 'danger' } {
  const isToday = isSameDay(startOfDay(nextActionAt), today);
  if (tab === 'today' || (tab === 'all' && isToday)) {
    return { labelKey: 'followups.statusTodo', variant: 'info' };
  }
  const daysLate = differenceInCalendarDays(today, startOfDay(nextActionAt));
  if (daysLate >= VERY_LATE_DAYS) {
    return { labelKey: 'followups.statusVeryLate', variant: 'danger' };
  }
  return { labelKey: 'followups.statusLate', variant: 'orange-pastel' };
}

function followUpIconVisual(variant: 'info' | 'orange-pastel' | 'danger') {
  return STAGE_ICON_VISUAL[FOLLOWUP_URGENCY_PIPELINE_STAGE[variant]];
}

const rowSlotClassName = `${FOLLOWUP_ROW_GRID} ${FOLLOWUP_ROW_SLOT_H}`;

/** Invisible row — reserves the same space as a populated row. */
const FollowUpRowPlaceholder: React.FC = () => (
  <div className={`${rowSlotClassName} invisible`} aria-hidden>
    <div className="h-8 w-8 shrink-0 rounded-full" />
    <p className="min-w-0 truncate text-sm font-medium">—</p>
    <span className={FOLLOWUP_DATE_CELL}>—</span>
    <div className="hidden min-w-0 sm:block" aria-hidden />
    <span className="hidden justify-self-start overflow-hidden sm:inline-flex">—</span>
    <div className="hidden min-w-0 sm:block" aria-hidden />
    <div className="flex shrink-0 items-center justify-end gap-0.5">
      <span className="inline-flex h-7 w-7" />
      <span className="inline-flex h-7 w-7" />
      <span className="inline-flex h-7 w-7" />
    </div>
  </div>
);

interface FollowUpRowProps {
  lead: Lead;
  tab: FollowUpTab;
  today: Date;
  categories: Category[];
  leadMenuActions?: LeadMenuActions;
  projects: any[];
  onEdit: (lead: Lead) => void;
  onDelete: (leadId: string) => void;
  onRelaunch: (lead: Lead) => void;
  onReschedule: (lead: Lead) => void;
  onLeadClick?: (lead: Lead) => void;
}

const FollowUpRow: React.FC<FollowUpRowProps> = ({
  lead,
  tab,
  today,
  categories,
  leadMenuActions,
  projects,
  onEdit,
  onDelete,
  onRelaunch,
  onReschedule,
  onLeadClick,
}) => {
  const { t } = useI18n();
  const dateLocale = useDateLocale();
  const nextActionAt = new Date(lead.nextActionAt!);
  const status = followUpStatus(tab, nextActionAt, today);
  const iconVisual = followUpIconVisual(status.variant);
  const contactCategory = categories.find((c) => c.id === lead.contact_type_id);
  const isToday = isSameDay(startOfDay(nextActionAt), today);

  return (
    <ContextMenuTarget<HTMLDivElement>>
      {(rowRef) => (
        <div
          ref={rowRef}
          className={`${rowSlotClassName} ${onLeadClick ? 'cursor-pointer rounded-lg transition-colors hover:bg-gray-50' : ''}`}
          data-lead-id={lead.id}
          role={onLeadClick ? 'button' : undefined}
          tabIndex={onLeadClick ? 0 : undefined}
          onClick={onLeadClick ? () => onLeadClick(lead) : undefined}
          onKeyDown={
            onLeadClick
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onLeadClick(lead);
                  }
                }
              : undefined
          }
        >
          <div
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${iconVisual.iconWrap}`}
          >
            {lead.contact_type_id && lead.contact_value ? (
              <ContactIconWithCategory
                category={contactCategory}
                value={lead.contact_value}
                size="sm"
                showTooltip={false}
                className={`scale-90 ${iconVisual.icon}`}
              />
            ) : (
              <Mail className={`h-3.5 w-3.5 ${iconVisual.icon}`} />
            )}
          </div>

          <p className="min-w-0 truncate text-sm font-medium text-gray-900">{lead.name}</p>

          <FollowUpDateCell
            date={nextActionAt}
            isToday={tab === 'today' || (tab === 'all' && isToday)}
            todayLabel={t('followups.today')}
            locale={dateLocale}
          />

          <div className="hidden min-w-0 sm:block" aria-hidden />

          <span
            className={`hidden items-center justify-self-start overflow-hidden whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium leading-4 sm:inline-flex ${followUpUrgencyBadgeVisual(status.variant)}`}
          >
            {t(status.labelKey)}
          </span>

          <div className="hidden min-w-0 sm:block" aria-hidden />

          <div
            className="flex shrink-0 items-center justify-end gap-0.5"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <ActionIconButton
              icon={MessageSquare}
              onClick={() => onRelaunch(lead)}
              variant="edit"
              size="sm"
              className="!p-1.5"
              tooltip={t('followups.relaunch')}
            />
            <ActionIconButton
              icon={Calendar}
              onClick={() => onReschedule(lead)}
              variant="edit"
              size="sm"
              className="!p-1.5"
              tooltip={t('leads.rescheduleFollowUp')}
            />
            <ContextMenu
              contextTargetRef={rowRef}
              items={getLeadActionMenuItems({
                lead,
                projects,
                contactCategory,
                t,
                context: 'followup',
                onContact: leadMenuActions?.onContact,
                onMessage: leadMenuActions?.onMessage,
                onCreateProject: leadMenuActions?.onCreateProject,
                onViewProject: leadMenuActions?.onViewProject,
                onMarkAsAbandoned: leadMenuActions?.onMarkAsAbandoned,
                onMarkAsRejected: leadMenuActions?.onMarkAsRejected,
                onMoveToNextStatus: leadMenuActions?.onMoveToNextStatus,
                onEdit,
                onDelete: (l) => onDelete(l.id),
              })}
              trigger="more"
              size="sm"
              tooltip={t('common.moreActions')}
            />
          </div>
        </div>
      )}
    </ContextMenuTarget>
  );
};

const FollowUpList: React.FC<FollowUpListProps> = ({
  leads,
  tab,
  categories,
  leadMenuActions,
  projects = [],
  onEdit,
  onDelete,
  onSeeOthers,
  onLeadClick,
}) => {
  const { t } = useI18n();
  const today = startOfDay(new Date());

  const displayedLeads = leads.slice(0, FOLLOWUP_PREVIEW_LIMIT);
  const hiddenCount = leads.length - displayedLeads.length;

  const handleRelaunch = (lead: Lead) => {
    if (leadMenuActions?.onMessage) {
      leadMenuActions.onMessage(lead);
      return;
    }
    leadMenuActions?.onContact?.(lead);
  };

  const handleReschedule = (lead: Lead) => {
    leadMenuActions?.onReschedule?.(lead);
  };

  const seeOthersLabel = t('followups.seeOthers', { count: hiddenCount > 0 ? hiddenCount : 1 });
  const rowSlots = Array.from({ length: FOLLOWUP_PREVIEW_LIMIT }, (_, index) => displayedLeads[index] ?? null);

  return (
    <div className="flex shrink-0 flex-col">
      <div className="relative shrink-0 divide-y divide-gray-100">
        {rowSlots.map((lead, index) =>
          lead ? (
            <FollowUpRow
              key={lead.id}
              lead={lead}
              tab={tab}
              today={today}
              categories={categories}
              leadMenuActions={leadMenuActions}
              projects={projects}
              onEdit={onEdit}
              onDelete={onDelete}
              onRelaunch={handleRelaunch}
              onReschedule={handleReschedule}
              onLeadClick={onLeadClick}
            />
          ) : (
            <FollowUpRowPlaceholder key={`placeholder-${index}`} />
          ),
        )}
        {leads.length === 0 ? (
          <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-center text-sm text-gray-500">
            {t('followups.emptyTab')}
          </p>
        ) : null}
      </div>

      <div className={`${FOLLOWUP_LIST_FOOTER_H} shrink-0 overflow-hidden pt-1`}>
        {hiddenCount > 0 ? (
          <button
            type="button"
            onClick={onSeeOthers}
            className="flex items-center gap-0.5 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            {seeOthersLabel}
            <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <span className="invisible flex items-center gap-0.5 text-sm font-medium" aria-hidden>
            {seeOthersLabel}
            <ChevronRight className="h-4 w-4" />
          </span>
        )}
      </div>
    </div>
  );
};

export default FollowUpList;
